import React from 'react';

import { useCms } from '~/components/hooks/useCms';
import useDataFetcher from '~/components/useDataFetcher';

export interface PredefinedPromptItem {
  prompt_title: string;
}

type PredefinedPromptsResponse = Record<string, PredefinedPromptItem>;

export interface UsePredefinedPromptsReturn {
  prompts: Record<string, PredefinedPromptItem>;
  isPromptsLoading: boolean;
  isPromptsError: boolean;
  reloadPrompts: () => Promise<PredefinedPromptsResponse | undefined>;
}

export const usePredefinedPrompts = (): UsePredefinedPromptsReturn => {
  const { userOrganization } = useCms();

  const baseRoute = `/api/v1/organizations/${userOrganization.id}/ai_configuration/predefined_prompts`;

  const { data, reloadData, isLoading, isError } = useDataFetcher<PredefinedPromptsResponse>(
    baseRoute,
    {},
    { shouldReportAxiosError: false }
  );

  const prompts = React.useMemo(() => {
    return data || {};
  }, [data]);

  return {
    prompts,
    isPromptsLoading: isLoading,
    isPromptsError: isError,
    reloadPrompts: reloadData,
  };
};

export const PredefinedPromptsContext = React.createContext<UsePredefinedPromptsReturn>({
  prompts: {},
  isPromptsLoading: false,
  isPromptsError: false,
  reloadPrompts: () => Promise.resolve(undefined),
});

interface PredefinedPromptsProviderProps {
  children: React.ReactNode;
}

export const PredefinedPromptsProvider: React.FC<PredefinedPromptsProviderProps> = ({ children }) => {
  const value = usePredefinedPrompts();
  return <PredefinedPromptsContext.Provider value={value}>{children}</PredefinedPromptsContext.Provider>;
};

export const usePredefinedPromptsContext = (): UsePredefinedPromptsReturn => {
  return React.useContext(PredefinedPromptsContext);
};
